// src/lib/permissions.js
// Team permission model.
//
// PERMISSION LEVELS:
//   owner    — full access, manages team, sees all roles, cannot be removed
//   manager  — sees all tasks within their allowedRoles, can create/assign, no team settings
//   staff    — sees own tasks only within their allowedRoles
//   external — PYHA participants (parents, players, coaches): own PYHA tasks only

export const PERMISSION_LEVELS = {
  owner:    { label: "Owner",    canAssign: true,  canManageTeam: true,  seeAllRoles: true,  seeAllTasks: true,  externalOnly: false },
  manager:  { label: "Manager",  canAssign: true,  canManageTeam: false, seeAllRoles: false, seeAllTasks: true,  externalOnly: false },
  staff:    { label: "Staff",    canAssign: false, canManageTeam: false, seeAllRoles: false, seeAllTasks: false, externalOnly: false },
  external: { label: "External", canAssign: false, canManageTeam: false, seeAllRoles: false, seeAllTasks: false, externalOnly: true  },
};

export function canSeeItem(member, item) {
  if (!member) return false;
  const perms = PERMISSION_LEVELS[member.permission] || PERMISSION_LEVELS.staff;

  // External: only own PYHA tasks — enforced unconditionally regardless of allowedRoles
  if (perms.externalOnly) {
    return item.roleId === "pyha" && item.assignedTo === member.uid;
  }

  // Owner / manager: see all tasks (role-tab filtering happens upstream via visibleRoles)
  if (perms.seeAllTasks) return true;

  // Staff: only items in allowedRoles, assigned to them or unassigned
  if (!member.allowedRoles?.includes(item.roleId)) return false;
  return !item.assignedTo || item.assignedTo === member.uid;
}

export function canAssign(member) {
  return PERMISSION_LEVELS[member?.permission]?.canAssign || false;
}

/** Who may triage escalations arriving from Practice Comms.
 *
 *  Deliberately its own predicate rather than a reuse of canAssign: this one has
 *  to stay identical to isOwnerOrManager in firestore.rules, which is the real
 *  gate. A UI that offers the inbox to someone the rules will refuse produces an
 *  opaque permission error; one that hides it from someone the rules allow just
 *  loses the work. */
export function canTriageIncoming(member) {
  return member?.permission === "owner" || member?.permission === "manager";
}

export function canManageTeam(member) {
  return PERMISSION_LEVELS[member?.permission]?.canManageTeam || false;
}

export function isExternal(member) {
  return member?.permission === "external";
}

const SENSITIVE_KEYWORDS = ["billing", "insurance", "medicaid", "payroll", "hr"];

// Returns the notes string, or "[Notes hidden]" if the member is external
// and the notes contain sensitive keywords.
export function filterNotes(notes, member) {
  if (!isExternal(member) || !notes) return notes;
  if (SENSITIVE_KEYWORDS.some(kw => notes.toLowerCase().includes(kw))) {
    return "[Notes hidden]";
  }
  return notes;
}
